import HEV_PRESETS_DATA from './hevPresets.json';

// HEV Energy Management Simulator: field metadata + client-side validation. Keys and ranges
// mirror backend/simulation/hev/schemas.py (HevVehicleIn, HevGearboxIn, HevEngineIn, HevMotorIn,
// HevBatteryIn, HevRuleIn, HevEcmsIn) exactly. Per-field validation reuses the EV Powertrain
// Simulator's validateField.
export { validateField, toRawStrings, DRIVE_TECHNOLOGY_OPTIONS } from './simulationTools';

export const VEHICLE_FIELD_SPECS = [
  { key: 'test_mass_kg', label: 'Test Mass', unit: 'kg', min: 500, max: 4000, strictlyPositive: true },
  { key: 'cd', label: 'Drag Coefficient (Cd)', unit: 'Cd', min: 0.15, max: 0.8, strictlyPositive: true },
  { key: 'frontal_area_m2', label: 'Frontal Area', unit: 'm²', min: 1.0, max: 5.0, strictlyPositive: true },
  { key: 'crr', label: 'Rolling Resistance Coeff.', unit: 'Crr', min: 0.003, max: 0.03, strictlyPositive: true },
  { key: 'wheel_radius_m', label: 'Tyre Rolling Radius', unit: 'm', min: 0.2, max: 0.5, strictlyPositive: true },
  { key: 'driveline_efficiency', label: 'Gearbox + Final Drive Efficiency', unit: '0-1', min: 0.8, max: 0.99, strictlyPositive: true },
];

export const GEARBOX_FIELD_SPECS = [
  { key: 'final_drive', label: 'Final Drive Ratio', unit: ':1', min: 1.5, max: 8.0, strictlyPositive: true },
  { key: 'upper_from_gear', label: '2nd Final Drive From Gear', unit: '0 = none', min: 0, max: 10, integer: true },
  { key: 'final_drive_upper', label: '2nd Final Drive Ratio', unit: ':1', min: 1.5, max: 8.0, optional: true },
  { key: 'shift_low_rpm', label: 'Upshift Speed, Light Load', unit: 'rpm', min: 800, max: 4000 },
  { key: 'shift_high_rpm', label: 'Upshift Speed, Full Load', unit: 'rpm', min: 1000, max: 6500 },
];
export const GEAR_RATIO_SPEC = { key: 'gear_ratios', label: 'Gear Ratios (1st → top)', unit: 'comma-separated', min: 0.3, max: 6.0 };

export const ENGINE_FIELD_SPECS = [
  { key: 'displacement_l', label: 'Displacement', unit: 'L', min: 0.6, max: 4.0, strictlyPositive: true },
  { key: 'max_power_kw', label: 'Max Power', unit: 'kW', min: 30, max: 300, strictlyPositive: true },
  { key: 'idle_rpm', label: 'Idle Speed (ICE baseline)', unit: 'rpm', min: 500, max: 1200 },
  { key: 'launch_rpm', label: 'Clutch Launch Speed', unit: 'rpm', min: 600, max: 2500 },
  { key: 'start_fuel_g', label: 'Fuel per Engine Start', unit: 'g', min: 0, max: 5 },
];

export const MOTOR_FIELD_SPECS = [
  { key: 'peak_power_kw', label: 'Motor Peak Power', unit: 'kW (0 = none)', min: 0, max: 200 },
  { key: 'peak_torque_nm', label: 'Motor Peak Torque', unit: 'Nm', min: 0, max: 600 },
];

export const BATTERY_FIELD_SPECS = [
  { key: 'energy_kwh', label: 'Pack Energy', unit: 'kWh (0 = none)', min: 0, max: 5 },
  { key: 'nominal_voltage_v', label: 'Nominal Voltage', unit: 'V', min: 48, max: 800, strictlyPositive: true },
  { key: 'internal_resistance_ohm', label: 'Internal Resistance', unit: 'Ω', min: 0, max: 2 },
  { key: 'max_discharge_kw', label: 'Max Discharge Power', unit: 'kW', min: 1, max: 200, strictlyPositive: true },
  { key: 'max_charge_kw', label: 'Max Charge Power', unit: 'kW', min: 1, max: 200, strictlyPositive: true },
  { key: 'soc_min_pct', label: 'Minimum SoC', unit: '%', min: 5, max: 90 },
  { key: 'soc_max_pct', label: 'Maximum SoC', unit: '%', min: 10, max: 95 },
  { key: 'soc_initial_pct', label: 'Initial SoC', unit: '%', min: 5, max: 95 },
  { key: 'aux_power_w', label: 'Accessory Load', unit: 'W', min: 0, max: 3000 },
];

export const RULE_FIELD_SPECS = [
  { key: 'engine_on_power_kw', label: 'Engine On Above', unit: 'kW', min: 0, max: 150 },
  { key: 'engine_off_power_kw', label: 'Engine Off Below', unit: 'kW', min: 0, max: 150 },
  { key: 'ev_max_speed_kph', label: 'EV Max Speed', unit: 'km/h', min: 0, max: 200 },
  { key: 'soc_low_pct', label: 'Low SoC (force engine on)', unit: '%', min: 5, max: 95 },
  { key: 'soc_target_pct', label: 'Target SoC', unit: '%', min: 5, max: 95 },
  { key: 'max_charge_power_kw', label: 'Max Load-Point Shift', unit: 'kW', min: 0, max: 100 },
  { key: 'assist_torque_frac', label: 'Assist Above (× engine max torque)', unit: '0-1', min: 0.3, max: 1.0 },
  { key: 'min_on_time_s', label: 'Min Engine-On Time', unit: 's', min: 0, max: 60 },
];
export const ECMS_S_SPEC = { key: 's_manual', label: 'Equivalence Factor s', unit: '–', min: 0.5, max: 8.0, strictlyPositive: true };

export const DRIVE_CYCLE_OPTIONS = [
  { value: 'wltc', label: 'WLTC (Class 3b)' },
  { value: 'midc', label: 'MIDC' },
  { value: 'us06', label: 'US06 (aggressive)' },
];
export const SAMPLING_OPTIONS = [
  { value: '1', label: '1.0 s' },
  { value: '0.5', label: '0.5 s' },
];
export const ARCHITECTURE_OPTIONS = [
  { value: 'p2', label: 'Parallel P2 (engine–clutch–motor–gearbox)' },
];
export const ENGINE_MAP_OPTIONS = [
  { value: 'epa_toyota_25_tnga_hybrid', label: 'EPA: Toyota 2.5 L TNGA hybrid (41 %)' },
  { value: 'epa_toyota_a25a_fks', label: 'EPA: Toyota 2.5 L A25A-FKS measured (39 %)' },
];
export const ECMS_MODE_OPTIONS = [
  { value: 'auto', label: 'Auto (tune s to charge-sustaining)' },
  { value: 'manual', label: 'Manual s' },
];

export const DEFAULT_RULE = {
  engine_on_power_kw: 16, engine_off_power_kw: 11, ev_max_speed_kph: 80, soc_low_pct: 47, soc_target_pct: 55,
  assist_torque_frac: 0.9, max_charge_power_kw: 10, min_on_time_s: 5,
};
export const DEFAULT_ECMS = { s_mode: 'auto', s_manual: 2.5 };

export const HEV_PRESETS = HEV_PRESETS_DATA;
export const DEFAULT_PRESET_ID = HEV_PRESETS[0].id;
export const findPreset = (id) => HEV_PRESETS.find((p) => p.id === id) || null;

export const STRATEGY_KEYS = ['rule', 'ecms', 'ice'];
export const STRATEGY_LABELS = { rule: 'Rule-based', ecms: 'ECMS', ice: 'ICE baseline' };

export const MIN_HEV_SOC_WINDOW_PCT = 10;

// Gearbox raw state keeps gear ratios as a comma-separated string.
export function gearboxToRaw(gb) {
  return {
    gear_ratios: gb.gear_ratios.join(', '),
    final_drive: String(gb.final_drive),
    final_drive_upper: gb.final_drive_upper == null ? '' : String(gb.final_drive_upper),
    upper_from_gear: String(gb.upper_from_gear ?? 0),
    shift_low_rpm: String(gb.shift_low_rpm),
    shift_high_rpm: String(gb.shift_high_rpm),
  };
}

export function parseGearRatios(raw) {
  const parts = String(raw || '').split(/[,\s;]+/).filter(Boolean);
  if (!parts.length) return { error: 'Enter at least one gear ratio.' };
  if (parts.length > 10) return { error: 'At most 10 gears.' };
  const values = parts.map(Number);
  if (values.some((v) => Number.isNaN(v))) return { error: 'Ratios must be numbers, separated by commas.' };
  if (values.some((v) => v < GEAR_RATIO_SPEC.min || v > GEAR_RATIO_SPEC.max)) {
    return { error: `Each ratio must be between ${GEAR_RATIO_SPEC.min} and ${GEAR_RATIO_SPEC.max}.` };
  }
  return { values };
}

// Mirrors the model validators in schemas.py; returns { group: { fieldKey: message } }.
export function validateCrossFields({ gearbox, engine, motor, battery, rule }, gearCount) {
  const errors = { gearbox: {}, engine: {}, motor: {}, battery: {}, rule: {} };
  if (gearbox.upper_from_gear > gearCount) errors.gearbox.upper_from_gear = 'Beyond the number of gears.';
  if (gearbox.upper_from_gear > 0 && (gearbox.final_drive_upper === undefined || gearbox.final_drive_upper === null)) {
    errors.gearbox.final_drive_upper = 'Required when a 2nd final drive is used.';
  }
  if (gearbox.shift_high_rpm < gearbox.shift_low_rpm) errors.gearbox.shift_high_rpm = 'Must be at least the light-load upshift speed.';
  if (engine.launch_rpm < engine.idle_rpm) errors.engine.launch_rpm = 'Must be at or above idle speed.';
  if ((motor.peak_power_kw > 0) !== (motor.peak_torque_nm > 0)) errors.motor.peak_torque_nm = 'Set both power and torque, or both to 0.';
  const { soc_min_pct: lo, soc_max_pct: hi, soc_initial_pct: init } = battery;
  if (hi - lo < MIN_HEV_SOC_WINDOW_PCT) errors.battery.soc_max_pct = `Must be at least ${MIN_HEV_SOC_WINDOW_PCT} points above Minimum SoC.`;
  if (!(init > lo && init < hi)) errors.battery.soc_initial_pct = 'Must be inside the SoC window.';
  if (rule.engine_off_power_kw > rule.engine_on_power_kw) errors.rule.engine_off_power_kw = 'Must not exceed Engine On Above.';
  if (rule.soc_low_pct >= rule.soc_target_pct) errors.rule.soc_low_pct = 'Must be below Target SoC.';
  if (!(lo <= rule.soc_low_pct && rule.soc_target_pct <= hi)) errors.rule.soc_target_pct = 'Low and Target SoC must sit inside the battery SoC window.';
  return errors;
}

// Operating modes from simulation/hev/p2.py MODES: short labels + colours for the mode band
// and the architecture diagram.
export const MODE_ORDER = ['stop', 'idle', 'ev', 'engine', 'assist', 'charge', 'regen', 'brake'];
export const MODE_SHORT = {
  stop: 'Stop', idle: 'Idle', ev: 'EV', engine: 'Engine', assist: 'Assist', charge: 'Charge', regen: 'Regen', brake: 'Brake',
};
